"use client";

import { clsx } from "clsx";
import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import { logClientError } from "@repo/next-utils/newrelic-client-utils";
import { useAuth } from "@repo/ui/auth";
import { Button } from "@repo/ui/button";
import { useStore } from "@repo/ui/store";
import { SierraAPI, SierraAPIConfig } from "../../sierra-web/types";
import { cleanupNotificationHandler } from "../../utils";
import {
  init,
  setChatWidgetParam,
  sierraChatTimeoutAction,
} from "../../utils/init";
import "./sierra-agent-global.css";
import { ChatErrorMessage } from "../chat-error-message/chat-error-message";
import styles from "./sierra-agent.module.css";

declare global {
  interface Window {
    sierra: SierraAPI;
    sierraConfig: SierraAPIConfig | undefined;
  }
}

export type SierraAgentProps = {
  handleChatBotStatusChange: (status: string) => void;
};

export const SierraAgent = ({
  handleChatBotStatusChange,
}: SierraAgentProps) => {
  const auth = useAuth();
  const store = useStore();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<Error | null>(null);
  const [showSierraChat, setShowSierraChat] = useState<boolean>(
    () => searchParams?.get("showChatWidget") === "true",
  );
  const [showOpenButton, setShowOpenButton] = useState(true);

  const toggleChatModal = () => {
    if (window.sierra) {
      setChatWidgetParam();
      window.sierra.openChatModal();
      handleChatBotStatusChange("chatactive");
      setIsLoading(false);
      setShowOpenButton(false);
    } else {
      const error = new Error("Sierra Chat: init error.");
      logClientError({ error });
      setInitError(error);
    }
  };

  useEffect(() => {
    if (auth.status === "initialising") {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;

    const initSierraSDK = async () => {
      if (!window.sierra) {
        try {
          await init({
            auth,
            searchParams,
            store,
            setShowSierraChat,
            toggleChatModal,
          });
          timeout = sierraChatTimeoutAction({
            searchParams,
            setInitError,
            setShowSierraChat,
          });
          setIsLoading(false);
        } catch (error) {
          setShowSierraChat(false);
          if (error instanceof Error) {
            logClientError({ error });
            setInitError(error);
          }
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    initSierraSDK();

    return () => {
      clearTimeout(timeout);
      cleanupNotificationHandler();
    };
  }, [auth, auth.status, searchParams, store]);

  if (initError) {
    return <ChatErrorMessage />;
  }

  return (
    <>
      {showOpenButton && (
        <Button
          className={clsx("typographyLabel3", styles.actionButton)}
          size="large"
          variant="secondary"
          alignment="start"
          disabled={isLoading}
          onClick={toggleChatModal}
        >
          {isLoading ? "Loading..." : "Open chat (online)"}
        </Button>
      )}
      <div
        id="sierra-chat-container"
        className={clsx(
          styles.sierraChatContainer,
          showOpenButton && !showSierraChat && styles.hidden,
        )}
        data-sierra-chat-container
      />
    </>
  );
};
